import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from './hooks/useDocumentTitle';

const TermsPage: React.FC = () => {
  const { t } = useTranslation('Terms');
  useDocumentTitle(t('pageTitle'));
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const sections = [
    { id: 'terms', title: t('termsTitle'), icon: '📋' },
    { id: 'privacy', title: t('privacyTitle'), icon: '🔒' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10 dark:from-blue-400/20 dark:to-purple-400/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            {/* Icon */}
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-2xl">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            
            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-4">
              {t('termsTitle')} & {t('privacyTitle')}
            </h1>
            
            {/* Effective Date Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full shadow-lg border border-white/20 dark:border-gray-700/20 mb-8">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                <strong>{t('effectiveDate')}</strong> {t('effectiveDateValue')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="flex flex-wrap justify-center gap-4">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
              className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-200 ${
                activeSection === section.id
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'bg-white/60 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 hover:bg-white/80 dark:hover:bg-gray-800/80'
              }`}
            >
              <span className="mr-2">{section.icon}</span>
              {section.title}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 dark:border-gray-700/20 overflow-hidden">
          
          {/* Terms of Service Section */}
          <div className="p-8 md:p-12">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('termsTitle')}</h1>
            </div>

            <div className="prose prose-lg max-w-none dark:prose-invert">
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                {t('termsIntro')}
              </p>

              {/* Terms Sections */}
              <div className="space-y-8">
                <section className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">1</span>
                    {t('useOfServiceTitle')}
                  </h2>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {t('useOfServiceItem1')}
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {t('useOfServiceItem2')}
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {t('useOfServiceItem3')}
                    </li>
                  </ul>
                </section>

                <section className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">2</span>
                    {t('accountsTitle')}
                  </h2>
                  <div className="space-y-4 text-gray-600 dark:text-gray-300">
                    <p>{t('accountsParagraph1')}</p>
                    <p>{t('accountsParagraph2')}</p>
                  </div>
                </section>

                <section className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">3</span>
                    {t('ownershipTitle')}
                  </h2>
                  <div className="space-y-4 text-gray-600 dark:text-gray-300">
                    <p>{t('ownershipParagraph1')}</p>
                    <p>{t('ownershipParagraph2')}</p>
                  </div>
                </section>

                <section className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">4</span>
                    {t('apiTitle')}
                  </h2>
                  <div className="space-y-4 text-gray-600 dark:text-gray-300">
                    <p>{t('apiParagraph1')}</p>
                    <p>{t('apiParagraph2')}</p>
                  </div>
                </section>

                <section className="bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">5</span>
                    {t('terminationTitle')}
                  </h2>
                  <div className="space-y-4 text-gray-600 dark:text-gray-300">
                    <p>{t('terminationParagraph1')}</p>
                    <p>{t('terminationParagraph2')}</p>
                  </div>
                </section>

                <section className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">6</span>
                    {t('disclaimersTitle')}
                  </h2>
                  <div className="space-y-4 text-gray-600 dark:text-gray-300">
                    <p>{t('disclaimersParagraph1')}</p>
                    <p>{t('disclaimersParagraph2')}</p>
                  </div>
                </section>

                <section className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">7</span>
                    {t('limitationTitle')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300">{t('limitationParagraph1')}</p>
                </section>

                <section className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">8</span>
                    {t('modificationsTitle')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300">{t('modificationsParagraph1')}</p>
                </section>

                <section className="bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-800/20 dark:to-slate-800/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">9</span>
                    {t('governingLawTitle')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300">{t('governingLawParagraph1')}</p>
                </section>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="px-8 md:px-12">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
          </div>

          {/* Privacy Policy Section */}
          <div className="p-8 md:p-12">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('privacyTitle')}</h1>
            </div>

            <div className="prose prose-lg max-w-none dark:prose-invert">
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                {t('privacyIntro')}
              </p>

              {/* Privacy Sections */}
              <div className="space-y-8">
                <section className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">1</span>
                    {t('whatWeCollectTitle')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{t('whatWeCollectIntro')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{t('accountInfo')}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{t('accountInfoDesc')}</p>
                    </div>
                    <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{t('usageData')}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{t('usageDataDesc')}</p>
                    </div>
                    <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{t('uploadedContent')}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{t('uploadedContentDesc')}</p>
                    </div>
                    <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{t('apiLogs')}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{t('apiLogsDesc')}</p>
                    </div>
                  </div>
                </section>

                <section className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">2</span>
                    {t('howWeUseTitle')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{t('howWeUseIntro')}</p>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {t('howWeUseItem1')}
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {t('howWeUseItem2')}
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {t('howWeUseItem3')}
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {t('howWeUseItem4')}
                    </li>
                  </ul>
                </section>

                <section className="bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">3</span>
                    {t('sharingTitle')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    We do <strong className="text-red-600 dark:text-red-400">{t('not')}</strong> sell your data. We may share data:
                  </p>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {t('sharingItem1')}
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {t('sharingItem2')}
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {t('sharingItem3')}
                    </li>
                  </ul>
                </section>

                {/* Additional Privacy Sections */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <section className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                      <span className="w-6 h-6 bg-green-600 rounded-lg flex items-center justify-center mr-3 text-white text-xs font-bold">4</span>
                      {t('retentionTitle')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300">{t('retentionParagraph1')}</p>
                  </section>

                  <section className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-2xl p-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                      <span className="w-6 h-6 bg-blue-600 rounded-lg flex items-center justify-center mr-3 text-white text-xs font-bold">5</span>
                      {t('securityTitle')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300">{t('securityParagraph1')}</p>
                  </section>

                  <section className="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-2xl p-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                      <span className="w-6 h-6 bg-purple-600 rounded-lg flex items-center justify-center mr-3 text-white text-xs font-bold">6</span>
                      {t('rightsTitle')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300">{t('rightsParagraph1')}</p>
                  </section>

                  <section className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-2xl p-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                      <span className="w-6 h-6 bg-orange-600 rounded-lg flex items-center justify-center mr-3 text-white text-xs font-bold">7</span>
                      {t('cookiesTitle')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300">{t('cookiesParagraph1')}</p>
                  </section>
                </div>

                <section className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">8</span>
                    {t('transfersTitle')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300">{t('transfersParagraph1')}</p>
                </section>

                <section className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">9</span>
                    {t('changesTitle')}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300">{t('changesParagraph1')}</p>
                </section>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-800/20 dark:to-slate-800/20 p-8 md:p-12">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                <strong>{t('contactUs')}</strong>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                <div className="flex items-center justify-center space-x-3 p-4 bg-white/60 dark:bg-gray-800/60 rounded-xl">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('email')}</p>
                    <p className="text-gray-900 dark:text-white font-semibold">{t('emailValue')}</p>
                  </div>
                </div>
                <div className="flex items-center justify-center space-x-3 p-4 bg-white/60 dark:bg-gray-800/60 rounded-xl">
                  <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('address')}</p>
                    <p className="text-gray-900 dark:text-white font-semibold">{t('addressValue')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
